/**
 * CommitBar — the only thing on this page that writes.
 *
 * IT IS DRAWN ONLY WHILE THERE IS SOMETHING TO COMMIT, which is what makes "unsaved" a state an
 * admin can see rather than a state they have to remember. Nothing on the setup canvas autosaves:
 * these values print onto legal instruments, so the confirmation stays explicit and the bar is
 * where it happens.
 *
 * SHARED RATHER THAN COPIED. It was written inside `FinancialsArea` and the parties block needs
 * exactly the same three affordances with exactly the same wording — a second copy is how the
 * two blocks would end up describing the same gesture two different ways.
 *
 * THREE OPTIONAL PROPS, ALL OF THEM FOR THE ADVANCE DOSSIER, AND ALL OF THEM DEFAULTING TO WHAT
 * THE SETUP CANVAS ALREADY DOES — so every existing call site is unchanged:
 *
 * - `saveLabel`, because the advance's create bar commits an advance INTO EXISTENCE rather than
 *   correcting one. "Save changes" over a button that mints a `loans` row, a `loan_instalments`
 *   row and a figure in `total_value` is the label describing the wrong gesture.
 * - `onDiscard` is optional, because a CREATE has nothing to revert to: the draft IS the page, and
 *   a Discard beside it would either clear a form nobody asked to clear or navigate away from a
 *   route the admin chose. Edits still pass it, and still get the button.
 * - `disabled`, because the create is refused by `validateDraft` before it is attempted [D-B54] and
 *   the bar says which field is missing and where it lives. A control that opened a proof-read
 *   dialog over an advance the writer would refuse is a dialog whose only outcome is a toast.
 */

import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface Props {
    saving: boolean;
    /** What this commit covers: `One commit for the whole block`, `2 fields commit together`. */
    hint: string;
    onSave: () => void;
    /** Absent draws no Discard at all — see the head. */
    onDiscard?: () => void;
    /** Defaults to the canvas's own `Save changes`. */
    saveLabel?: string;
    /** Refused before it is attempted. `saving` disables on its own, as it always has. */
    disabled?: boolean;
}

export default function CommitBar({
    saving,
    hint,
    onSave,
    onDiscard,
    saveLabel = 'Save changes',
    disabled,
}: Props) {
    return (
        <div className="sd-commit">
            <Button size="sm" onClick={onSave} disabled={saving || disabled}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saveLabel}
            </Button>
            {onDiscard && (
                <Button variant="ghost" size="sm" onClick={onDiscard} disabled={saving}>
                    Discard
                </Button>
            )}
            <span className="sd-cs-hint">{hint}</span>
        </div>
    );
}
